import { getAssignmentByAdmissionId } from "./PatientAssignmentService";
import { getAppointmentsGroupedByPatient } from "./appointmentService";
import { getAll as getAllAdmissions } from "./PatientAdmissionService";
import { getAllDoctors } from "./doctorService";
import { getAllDepartment } from "./departmentService";
import { getAllPatients } from "./patientService";
import {
  EMPTY_PATIENT_PERIOD_SUMMARY,
  buildAlignedOpdPeriodExport,
  buildDepartmentsLookup,
  buildDoctorsLookup,
  buildIpdSummaryRecords,
  computePatientPeriodSummary,
  filterIpdAdmissionsInPeriod,
  getAdmissionDate,
  isDateInRange,
  normalizeDateValue,
  parseMonthYearBounds,
  pickLatestAssignment,
  toPeriodDateKey,
} from "../utils/patientPeriodSummary";
import { buildIdLookupMap, fetchPeriodInvoicesForSummary } from "../utils/invoiceTableUtils";

const assignmentByAdmissionCache = new Map();
const assignmentByAdmissionInflight = new Map();
const opdSummaryCache = new Map();
const opdSummaryInflight = new Map();
const ipdSummaryCache = new Map();
const ipdSummaryInflight = new Map();
const quickPatientSummaryCache = new Map();
const quickPatientSummaryInflight = new Map();

function getDischargeDate(admission) {
  if (!admission) return null;
  return normalizeDateValue(
    admission.dischargeDate ||
      admission.dischargedAt ||
      admission.discharge_date ||
      admission.dateOfDischarge
  );
}

function countIpdAdmittedDischarged(allAdmissions, periodStart, periodEnd) {
  const list = Array.isArray(allAdmissions) ? allAdmissions : [];
  let admitted = 0;
  let discharged = 0;

  list.forEach((admission) => {
    const status = String(admission?.status || "").toLowerCase().trim();
    const admissionDate = getAdmissionDate(admission);
    const dischargeDate = getDischargeDate(admission);

    if (isDateInRange(admissionDate, periodStart, periodEnd)) {
      admitted += 1;
    }

    const isDischarged = status === "discharge" || status === "discharged";
    if (!isDischarged) return;
    const dateForDischarge = dischargeDate || admissionDate;
    if (isDateInRange(dateForDischarge, periodStart, periodEnd)) {
      discharged += 1;
    }
  });

  return { admitted, discharged };
}

export async function fetchAssignmentsByAdmissionIds(admissionIds, batchSize = 10) {
  const map = {};
  const uniqueIds = Array.from(new Set((admissionIds || []).filter(Boolean)));

  for (let i = 0; i < uniqueIds.length; i += batchSize) {
    const batch = uniqueIds.slice(i, i + batchSize);
    const results = await Promise.allSettled(
      batch.map(async (admissionId) => {
        if (assignmentByAdmissionCache.has(admissionId)) {
          return {
            admissionId,
            assignment: assignmentByAdmissionCache.get(admissionId),
          };
        }

        if (assignmentByAdmissionInflight.has(admissionId)) {
          const inflightAssignment = await assignmentByAdmissionInflight.get(admissionId);
          return {
            admissionId,
            assignment: inflightAssignment,
          };
        }

        const request = getAssignmentByAdmissionId(admissionId)
          .then((assignment) => pickLatestAssignment(assignment))
          .catch(() => null)
          .finally(() => {
            assignmentByAdmissionInflight.delete(admissionId);
          });

        assignmentByAdmissionInflight.set(admissionId, request);
        const assignment = await request;
        assignmentByAdmissionCache.set(admissionId, assignment ?? null);
        return {
          admissionId,
          assignment,
        };
      })
    );

    results.forEach((result) => {
      if (result.status === "fulfilled" && result.value?.assignment) {
        map[result.value.admissionId] = result.value.assignment;
      }
    });
  }

  return map;
}

export async function fetchOpdPeriodSummary(month, year) {
  const cacheKey = `${year}-${month}`;
  if (opdSummaryCache.has(cacheKey)) {
    return opdSummaryCache.get(cacheKey);
  }
  if (opdSummaryInflight.has(cacheKey)) {
    return opdSummaryInflight.get(cacheKey);
  }

  const request = (async () => {
  try {
    const { start, end } = parseMonthYearBounds(month, year);
    const periodStartKey = toPeriodDateKey(start);
    const periodEndKey = toPeriodDateKey(end);
    // Same path as PatientsTable Excel "Outpatient Summary" (invoice-aligned monthly counts)
    const [groupedAppointments, doctors, departments, patients, invoiceData] = await Promise.all([
      getAppointmentsGroupedByPatient().catch(() => ({})),
      getAllDoctors({ forceRefresh: false }).catch(() => []),
      getAllDepartment().catch(() => []),
      getAllPatients().catch(() => []),
      fetchPeriodInvoicesForSummary(periodStartKey, periodEndKey).catch(() => []),
    ]);

    const doctorsById = buildDoctorsLookup(doctors);
    const departmentsById = buildDepartmentsLookup(departments);
    const patientDetailsMap = buildIdLookupMap(patients);
    const { summary } = buildAlignedOpdPeriodExport({
      groupedAppointments,
      invoices: Array.isArray(invoiceData) ? invoiceData : [],
      patientDetailsMap,
      doctorsById,
      departmentsById,
      periodStart: start,
      periodEnd: end,
      periodStartKey,
      periodEndKey,
    });
    return summary || { ...EMPTY_PATIENT_PERIOD_SUMMARY };
  } catch (error) {
    console.error("[patientPeriodSummaryService] Failed to fetch OPD summary:", error);
    return { ...EMPTY_PATIENT_PERIOD_SUMMARY };
  }
  })();

  opdSummaryInflight.set(cacheKey, request);
  const result = await request.finally(() => {
    opdSummaryInflight.delete(cacheKey);
  });
  opdSummaryCache.set(cacheKey, result);
  return result;
}

const formatTrendChange = (current, previous) => {
  const curr = Number(current) || 0;
  const prev = Number(previous) || 0;
  if (prev <= 0) {
    if (curr <= 0) return { change: "0%", trend: "up" };
    return { change: "100%", trend: "up" };
  }
  const pct = Math.round(((curr - prev) / prev) * 1000) / 10;
  return {
    change: `${Math.abs(pct)}%`,
    trend: pct < 0 ? "down" : "up",
  };
};

/**
 * Quick Patient Summary for dashboard — OPD total/new/returning match
 * PatientsTable Excel "Outpatient Summary" (invoice-aligned).
 * Admitted/Discharged remain IPD-only.
 */
export async function fetchQuickPatientSummary(month, year) {
  const cacheKey = `${year}-${month}`;
  if (quickPatientSummaryCache.has(cacheKey)) {
    return quickPatientSummaryCache.get(cacheKey);
  }
  if (quickPatientSummaryInflight.has(cacheKey)) {
    return quickPatientSummaryInflight.get(cacheKey);
  }

  const request = (async () => {
  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear = month === 0 ? year - 1 : year;

  const [opdCurrent, ipdCurrent, opdPrev] = await Promise.all([
    fetchOpdPeriodSummary(month, year),
    fetchIpdPeriodSummary(month, year),
    fetchOpdPeriodSummary(prevMonth, prevYear),
  ]);

  // Match Outpatient Summary export (do not mix IPD into total/new/returning)
  const totalPatients = opdCurrent.total || 0;
  const newPatients = opdCurrent.new || 0;
  const oldPatients = opdCurrent.returning || 0;

  const prevTotal = opdPrev.total || 0;
  const prevNew = opdPrev.new || 0;
  const prevOld = opdPrev.returning || 0;

  const totalTrend = formatTrendChange(totalPatients, prevTotal);
  const newTrend = formatTrendChange(newPatients, prevNew);
  const oldTrend = formatTrendChange(oldPatients, prevOld);

  return {
    totalPatients,
    newPatients,
    oldPatients,
    admittedPatients: Number(ipdCurrent.admitted) || 0,
    dischargedPatients: Number(ipdCurrent.discharged) || 0,
    totalChange: totalTrend.change,
    newChange: newTrend.change,
    oldChange: oldTrend.change,
    totalTrend: totalTrend.trend,
    newTrend: newTrend.trend,
    oldTrend: oldTrend.trend,
    opdSummary: opdCurrent,
    ipdSummary: ipdCurrent,
  };
  })();

  quickPatientSummaryInflight.set(cacheKey, request);
  const result = await request.finally(() => {
    quickPatientSummaryInflight.delete(cacheKey);
  });
  quickPatientSummaryCache.set(cacheKey, result);
  return result;
}

export async function fetchIpdPeriodSummary(month, year) {
  const cacheKey = `${year}-${month}`;
  if (ipdSummaryCache.has(cacheKey)) {
    return ipdSummaryCache.get(cacheKey);
  }
  if (ipdSummaryInflight.has(cacheKey)) {
    return ipdSummaryInflight.get(cacheKey);
  }

  const request = (async () => {
  try {
    const { start, end } = parseMonthYearBounds(month, year);
    const [admissions, doctors, departments] = await Promise.all([
      getAllAdmissions().catch(() => []),
      getAllDoctors({ forceRefresh: false }).catch(() => []),
      getAllDepartment().catch(() => []),
    ]);

    const doctorsById = buildDoctorsLookup(doctors);
    const departmentsById = buildDepartmentsLookup(departments);
    const allAdmissions = Array.isArray(admissions) ? admissions : [];
    const rows = filterIpdAdmissionsInPeriod(allAdmissions, start, end);
    const assignmentsByAdmissionId = await fetchAssignmentsByAdmissionIds(
      rows.map((row) => row.id)
    );
    const records = buildIpdSummaryRecords({
      rows,
      allAdmissions,
      doctorsById,
      departmentsById,
      periodStart: start,
      assignmentsByAdmissionId,
    });

    const summary = computePatientPeriodSummary(records);
    const { admitted, discharged } = countIpdAdmittedDischarged(allAdmissions, start, end);
    return {
      ...summary,
      admitted,
      discharged,
    };
  } catch (error) {
    console.error("[patientPeriodSummaryService] Failed to fetch IPD summary:", error);
    return { ...EMPTY_PATIENT_PERIOD_SUMMARY, admitted: 0, discharged: 0 };
  }
  })();

  ipdSummaryInflight.set(cacheKey, request);
  const result = await request.finally(() => {
    ipdSummaryInflight.delete(cacheKey);
  });
  ipdSummaryCache.set(cacheKey, result);
  return result;
}
